import { z } from 'zod';
/**
 * `GET /api/admin/hub-queue` — admin view of plugins whose sync-repo Hub
 * PR is open but not yet merged (`hub_pr_url IS NOT NULL AND hub_listed_at
 * IS NULL`). Operator surface for tracking Hub PRs awaiting marketplace-
 * admin review. Per ADR-0003 + Task 12 of the Hub bootstrap plan.
 *
 * Gate: `admin:view`. The endpoint is read-only — no audit row, no
 * mutation. The default sort is newest-first (`created_at DESC`); the UI
 * may re-sort but the wire order is the operationally most-useful one
 * (most recently opened PRs at the top).
 */
export const hubQueueItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    hubPrUrl: z.string().url(),
    teamDisplayName: z.string().nullable(),
    createdAt: z.string().datetime(),
    /**
     * Stamped by the reconcile-sync sweep when the row's Hub PR was abandoned
     * in ADO. The row stays hub-pending (hidden from catalog/manifests); this
     * field lets operators distinguish "awaiting review" from "rejected —
     * needs a new team-repo push". Cleared when a later sync opens a new PR
     * or the hub mirror lists/unlists the row.
     */
    abandonedAt: z.string().datetime().nullable(),
});
export const hubQueueResponseSchema = z.object({
    items: z.array(hubQueueItemSchema),
    /**
     * Count of plugins that have never been published to the Hub at all —
     * both `hub_pr_url` and `hub_listed_at` are NULL on an active,
     * non-deleted source (see `getHubQueueCounts` in
     * `marketplace-api/src/modules/admin/hub-queue.service.ts`). This is the
     * blast-radius number an operator checks before flipping the
     * `hub-unpublished-backfill` flag ON: it is the pure-DB form, not an
     * exact Hub-manifest comparison, so it slightly OVER-counts (a row
     * published out-of-band still appears here) — the right error direction
     * for a pre-flight estimate. The exact manifest comparison remains the
     * reconcile mirror pass's job.
     *
     * NOT a PR count. The backfill enqueues one sync per candidate SOURCE
     * (each opening at most one Hub PR), and its own query narrows further:
     * it requires `plugin_sources.last_sync_sha IS NOT NULL`, applies a 24h
     * per-source `hub_backfill_at` backoff, and dedups per source. So the
     * PRs a first run actually opens are bounded by the number of distinct
     * sources behind this count, not by the count itself. Presenting this
     * number as "PRs about to open" overstates it.
     */
    neverPublishedCount: z.number().int().nonnegative(),
    /**
     * Count of hub-pending rows (`hub_pr_url` set, `hub_listed_at` NULL)
     * whose PR has been open longer than `longOpenThresholdHours`. This
     * counts PR AGE, not stalled-ness — it cannot distinguish "the
     * create-time auto-complete PATCH failed" from "a human is still
     * reviewing it" (that distinction lives in ADO's `autoCompleteSetBy`,
     * which this system does not persist). With `hub-pr-auto-complete` OFF
     * the product is review-first, so a non-zero count here is EXPECTED — a
     * review backlog, not a fault. Rows with `hub_pr_opened_at IS NULL` are
     * excluded because their age is unknown, not because it is zero.
     */
    longOpenPrCount: z.number().int().nonnegative(),
    /**
     * The threshold (in hours) `longOpenPrCount` was computed against —
     * sourced from `HUB_PR_STALE_AFTER_HOURS`, not a client-side constant, so
     * a future admin-dashboard surface can label the count from the payload
     * itself rather than hard-coding the number.
     */
    longOpenThresholdHours: z.number().int().positive(),
});
/**
 * `GET /api/admin/hub/orphans` — admin view of the persisted
 * `hub_orphan_listings` table: names present in the Hub `marketplace.json`
 * that have no matching catalog row (e.g. a plugin the sync validator skipped
 * on a secret-scan finding, never upserted). Distinct from the hub-queue
 * (pending PRs): an orphan is *listed* in the Hub but *missing* from our
 * catalog. Upserted by the reconcile mirror pass; cleared when the catalog
 * row appears or the name leaves the Hub. `firstSeenAt` gives the operator
 * an age signal for triage.
 *
 * Gate: `admin:view`. Read-only — no audit row, no mutation. Sorted oldest
 * first (`first_seen_at ASC`) so the longest-orphaned rows surface at the top.
 */
export const hubOrphanSchema = z.object({
    name: z.string(),
    source: z.string(),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
});
export const hubOrphansResponseSchema = z.object({
    items: z.array(hubOrphanSchema),
});
/**
 * `POST /api/admin/hub-queue/:pluginId/force-auto-complete` (Task 10,
 * ADR-0003) — operator escape hatch that turns the manual `PATCH` used to
 * unblock four stalled Hub PRs on 2026-07-29 into a supported action. ADO's
 * PR *create* endpoint silently discards `completionOptions`/
 * `autoCompleteSetBy`; this route re-issues the PATCH
 * (`AdoClient.setPullRequestAutoComplete`) for exactly the cases the
 * automation deliberately does not handle on its own — `hub-pr-auto-complete`
 * is off but this particular PR should still merge, or the reconcile-sync
 * self-heal sweep (Task 6) is itself failing and an operator needs to act now.
 *
 * Deliberately keyed by `pluginId`, never a raw ADO PR id. Accepting a PR id
 * would let an operator set auto-complete on ANY PR in the Hub repo,
 * including a human's. Resolving through the plugin's `hub_pr_url` instead
 * constrains the action to rollup PRs the marketplace itself opened — a
 * security property, not a style choice.
 *
 * Gate: `admin:sync` (reused — the same permission `POST
 * /api/admin/sync/sources/:id` uses for "make the sync machinery do
 * something now"). Mutates ADO state (merges to Hub `main` once branch
 * policies pass) and writes a `plugin.hub_pr_force_auto_complete` audit row
 * — but only AFTER the ADO call succeeds, so the row never asserts an
 * ADO-side change that didn't happen.
 *
 * - `404` — the plugin row is missing, soft-deleted, or has no open Hub PR
 *   (`hub_pr_url IS NULL`).
 * - `400` — `hub_pr_url` is set but no numeric PR id could be parsed from it.
 * - `502` — ADO rejected the auto-complete PATCH.
 * - `503` — the route's ADO client / Hub repo id are not configured (e.g.
 *   local dev with `MARKETPLACE_HUB_REPO_ID` unset).
 */
export const forceHubPrAutoCompleteParamsSchema = z.object({ pluginId: z.string().uuid() });
export const forceHubPrAutoCompleteResponseSchema = z.object({
    pullRequestId: z.number().int().positive(),
});
//# sourceMappingURL=admin.js.map